import { Link } from 'react-router-dom'

import type { RegistroDeAuditoria } from '@/lib/supabase/tipos-bd'
import { horaCorta } from '@/shared/utilidades/formato'

import { frase } from './frases'
import { Avatar, nombreDelRol } from './Persona'
import { diaEnCartago } from './useAuditoria'
import estilos from './PaginaAuditoria.module.css'

/**
 * LA LÍNEA DE TIEMPO: día por día, lo más nuevo arriba.
 *
 * Cada renglón se lee como una oración —«Jerson Teran · Gerente operativo
 * programó la orden de trabajo OT-2026-0061»—, con la hora a la izquierda y
 * la nota, si la hay, debajo y entre comillas, porque es lo que esa persona
 * escribió y así se respeta. El número abre la orden.
 */
const FORMATO_DIA = new Intl.DateTimeFormat('es-CO', {
  timeZone: 'UTC',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})

export function LineaDeTiempo({ registros }: { registros: RegistroDeAuditoria[] }) {
  const porDia = new Map<string, RegistroDeAuditoria[]>()
  for (const r of registros) {
    const dia = diaEnCartago(r.ocurrido_en)
    porDia.set(dia, [...(porDia.get(dia) ?? []), r])
  }

  return (
    <div className={estilos.linea}>
      {[...porDia.entries()].map(([dia, lista]) => (
        <section key={dia} className={estilos.dia}>
          <h3 className={estilos.diaTitulo}>
            {FORMATO_DIA.format(new Date(`${dia}T00:00:00Z`))}
            <span>
              {lista.length} {lista.length === 1 ? 'acción' : 'acciones'}
            </span>
          </h3>
          <ol className={estilos.registros}>
            {lista.map((r) => (
              <Registro key={r.id} r={r} />
            ))}
          </ol>
        </section>
      ))}
    </div>
  )
}

function Registro({ r }: { r: RegistroDeAuditoria }) {
  const { verbo, documento } = frase(r)
  const numero = r.documento_numero ?? 'sin número'
  const esNota = r.tipo === 'nota'

  return (
    <li className={`${estilos.registro} ${estilos[`tipo_${r.tipo}`] ?? ''}`}>
      <time className={estilos.hora} dateTime={r.ocurrido_en}>
        {horaCorta(r.ocurrido_en)}
      </time>
      <Avatar nombre={r.actor_nombre} rol={r.actor_rol} />
      <div className={estilos.cuerpo}>
        <p className={estilos.oracion}>
          <b>{r.actor_nombre}</b>
          <span className={estilos.rol}>{nombreDelRol(r.actor_rol)}</span>{' '}
          {esNota ? 'anotó en' : verbo} {documento}{' '}
          {r.orden_servicio_id ? (
            <Link to={`/ordenes/${r.orden_servicio_id}`} className={estilos.numero}>
              {numero}
            </Link>
          ) : (
            <span className={estilos.numero}>{numero}</span>
          )}
        </p>
        {r.notas && <p className={estilos.nota}>«{r.notas}»</p>}
      </div>
    </li>
  )
}
